import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Icons } from "../components/Icons";
import { TableSkeleton, AdminOrganizationsSkeleton } from "../components/SkeletonLoader";
import {
  getAllOrganizations,
  getAllUsers,
  updateOrganization,
  deleteOrganization,
} from "../services/api";
import { downloadOrgDetails, downloadByDocumentType, downloadAllByType, downloadOrgList, downloadAllOrgs } from '../services/api';
import { uploadAccreditationCertificate, getAccreditationCertificate, deleteAccreditationCertificate, getCurrentAcademicYear } from '../services/api';

// Drag-and-drop Accreditation Certificate upload area, extracted into its own
// component so its useState/useRef hooks aren't called conditionally (the
// modal that hosts it only mounts while accredCertModalOrg is set).
const AccreditationCertUploadDropzone = ({ orgId, accredCerts, accredCertUploading, fetchAccredCert, setAccredCertUploading }) => {
  const [dragOver, setDragOver] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState(null);
  const fileInputRef = React.useRef(null);

  const handleUpload = async () => {
    if (!selectedFile) return;
    setAccredCertUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      await uploadAccreditationCertificate(orgId, formData);
      await fetchAccredCert(orgId);
      setSelectedFile(null);
    } catch (error) {
      alert('Failed to upload certificate.');
    } finally {
      setAccredCertUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div
        className={`border-2 border-dashed rounded-2xl p-6 flex flex-col items-center justify-center cursor-pointer transition-all duration-200 ${dragOver ? 'border-[#086C53] bg-[#086C53]/5'
          : selectedFile ? 'border-[#086C53] bg-[#086C53]/5'
            : 'border-gray-300 hover:border-[#086C53] hover:bg-[#086C53]/5'
          }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files[0]) setSelectedFile(e.dataTransfer.files[0]);
        }}
        onClick={() => fileInputRef.current.click()}
      >
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
          onChange={(e) => { if (e.target.files[0]) setSelectedFile(e.target.files[0]); e.target.value = ''; }}
        />
        <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${selectedFile ? 'bg-[#086C53]/15' : 'bg-[#086C53]/10'}`}>
          <svg className="w-6 h-6 text-[#086C53]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
        </div>
        {selectedFile ? (
          <p className="text-[13px] font-semibold text-gray-700 text-center">{selectedFile.name}</p>
        ) : (
          <p className="text-[13px] text-gray-500 text-center">
            Drag and drop certificate here, or{' '}
            <span className="text-[#086C53] font-semibold underline">Browse</span>
          </p>
        )}
        <p className="text-[11px] text-gray-400 mt-1">Max 10MB</p>
      </div>
      {selectedFile && (
        <div className="flex gap-2">
          <button
            onClick={handleUpload}
            disabled={accredCertUploading}
            className="flex-1 py-3 rounded-xl text-[14px] font-bold text-white transition-colors disabled:opacity-60 bg-[#054334] hover:bg-[#086C53]"
          >
            {accredCertUploading ? 'Uploading...' : accredCerts[orgId] ? 'Replace Certificate' : 'Upload Certificate'}
          </button>
          <button
            onClick={() => setSelectedFile(null)}
            className="px-4 py-3 rounded-xl text-[13px] font-bold text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
};

// --- Organizations Page ---
const Organizations = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState("College");
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [editingOrg, setEditingOrg] = useState(null);
  const [deletingOrg, setDeletingOrg] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const itemsPerPage = 8;

  const [accredCertModalOrg, setAccredCertModalOrg] = useState(null);
  const [accredCerts, setAccredCerts] = useState({});
  const [accredCertUploading, setAccredCertUploading] = useState(false);
  const [accredCertDeleting, setAccredCertDeleting] = useState(false);

  // 3-dots Action menu state
  const [actionMenuOrg, setActionMenuOrg] = useState(null);
  const [actionMenuPos, setActionMenuPos] = useState({ x: 0, y: 0, flip: false });

  useEffect(() => {
    const handleCloseMenu = () => setActionMenuOrg(null);
    window.addEventListener("scroll", handleCloseMenu, true);
    window.addEventListener("resize", handleCloseMenu);
    return () => {
      window.removeEventListener("scroll", handleCloseMenu, true);
      window.removeEventListener("resize", handleCloseMenu);
    };
  }, []);

  const fetchAccredCert = async (orgId) => {
    try {
      const res = await getAccreditationCertificate(orgId);
      setAccredCerts(prev => ({ ...prev, [orgId]: res.data }));
    } catch (error) {
      setAccredCerts(prev => ({ ...prev, [orgId]: null }));
    }
  };

  // Real data from the backend instead of mock data
  const [orgData, setOrgData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search input value for filtering organizations by name
  const [searchTerm, setSearchTerm] = useState("");

  // Holds the edit form input values
  const [editForm, setEditForm] = useState({
    name: "",
    acronym: "",
    college: "",
    contact_email: "",
  });

  const [downloadingOrgId, setDownloadingOrgId] = useState(null);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadingDocType, setDownloadingDocType] = useState(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingAllByType, setDownloadingAllByType] = useState(false);
  const [downloadingOrgList, setDownloadingOrgList] = useState(false);

  const handleDownloadOrg = async (orgId, orgName) => {
    setDownloadingOrgId(orgId);
    try {
      const response = await downloadOrgDetails(orgId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const safeName = orgName.replace(/\s+/g, '_');
      link.setAttribute('download', `${safeName}-details.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download organization details.');
    } finally {
      setDownloadingOrgId(null);
    }
  };

  // Creates initials from a full name (e.g. "Naomi Kate Tan" -> "NT")
  const getInitials = (name) => {
    if (!name) return "??";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Fetches organizations and their representative users from the backend
  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const orgsRes = await getAllOrganizations();
      const usersRes = await getAllUsers();
      const users = usersRes.data;

      // Only include organizations that have been endorsed or scheduled for interview.
      // Pending organizations (just accepted to access the site) are NOT listed here.
      const eligibleOrgs = orgsRes.data.filter((org) =>
        org.status === "for_endorsement_accredited" ||
        org.status === "for_endorsement_reaccredited" ||
        org.status === "for_interview"
      );

      // Maps each organization to the table row format the UI expects
      const mapped = eligibleOrgs.map((org) => {
        const rep = users.find(
          (u) =>
            u.organization_id === org.organization_id && u.role === "end_user",
        );
        return {
          id: org.organization_id,
          repName: rep ? rep.full_name : "No representative",
          repInitials: rep ? getInitials(rep.full_name) : "--",
          org: org.acronym || org.name,
          orgFullName: org.name,
          email: org.contact_email || (rep ? rep.slsu_email : "N/A"),
          college: org.college || "N/A",
          acronym: org.acronym || "",
          status:
            org.status === "for_endorsement_accredited"
              ? "Accredited"
              : org.status === "for_endorsement_reaccredited"
                ? "Re-accredited"
                : "For Interview",
          description: org.description || "",
          orgCategory: org.organization_category || "",
          presidentContact: org.president_contact || "N/A",
          adviserName: org.adviser_name || "N/A",
          adviserContact: org.adviser_contact || "N/A",
          totalMembers: org.total_members !== null && org.total_members !== undefined ? org.total_members : "N/A",
        };
      });

      setOrgData(mapped);

      // Pre-fetch accreditation certs for all listed orgs
      eligibleOrgs.forEach(org => {
        fetchAccredCert(org.organization_id || org.id);
      });
    } catch (error) {
      console.error("Failed to fetch organizations:", error);
    } finally {
      setLoading(false);
    }
  };

  // Runs fetchOrganizations once when the page loads
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchOrganizations();
  }, []);

  // Filters organizations by search term and selected status filter
  const filteredData = orgData.filter((row) => {
    // Search filter — matches organization name or representative name
    const matchesSearch =
      row.org.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.repName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (row.college && row.college.toLowerCase().includes(searchTerm.toLowerCase()));

    // Status filter
    let matchesFilter = true;
    if (selectedFilter === "Accredited") matchesFilter = row.status === "Accredited";
    else if (selectedFilter === "Re-accredited")
      matchesFilter = row.status === "Re-accredited";
    else if (selectedFilter === "For Interview")
      matchesFilter = row.status === "For Interview";
    // College/Campus filters match against the description field
    else if (
      selectedFilter !== "All Status" &&
      selectedFilter !== "College" &&
      selectedFilter !== "Campus"
    ) {
      matchesFilter =
        row.college &&
        row.college.toUpperCase().includes(selectedFilter.toUpperCase());
    }

    return matchesSearch && matchesFilter;
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const currentData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handlePrev = () => setCurrentPage((prev) => Math.max(1, prev - 1));
  const handleNext = () =>
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  const handlePageClick = (page) => setCurrentPage(page);

  // Opens the edit modal and pre-fills the form with the organization's current data
  const openEditModal = (row) => {
    setEditForm({
      name: row.orgFullName,
      acronym: row.acronym,
      college: row.college,
      contact_email: row.email,
    });
    setEditingOrg(row);
  };

  // Saves the edited organization details to the backend

  const handleSaveEdit = async () => {
    try {
      await updateOrganization(editingOrg.id, {
        name: editForm.name,
        acronym: editForm.acronym,
        college: editForm.college,
        contact_email: editForm.contact_email,
      });
      await fetchOrganizations();
      setEditingOrg(null);
    } catch (error) {
      console.error("Failed to update organization:", error);
      alert("Failed to save changes. Please try again.");
    }
  };

  // Deletes the organization from the backend
  const handleConfirmDelete = async () => {
    if (!deletingOrg || isDeleting) return;
    try {
      setIsDeleting(true);
      await deleteOrganization(deletingOrg.id);
      await fetchOrganizations();
      setDeletingOrg(null);
    } catch (error) {
      console.error("Failed to delete organization:", error);
      const msg =
        error.response?.data?.detail ||
        "Failed to delete organization. Please try again.";
      alert(msg);
    } finally {
      setIsDeleting(false);
    }
  };

  const ACCREDITATION_DOCUMENTS = [
    "Waiver",
    "Roster of Officers",
    "Roster of Members",
    "Financial Statement",
    "General Plan of Activities (GPOA)",
    "Summary of GPOA",
    "Checklist for Student Org Accreditation",
    "Application for Accreditation Letter",
    "Request Letter for Adviser",
    "Acceptance Letter",
    "Officers' Contact Details",
    "Narrative Report",
    "Three (3) Attended Seminars",
    "Inventory of Materials",
    "Constitution and By-laws Amendment",
  ];

  const handleDownloadByDocType = (docName) => {
    setDownloadingDocType(docName);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://orgpass.tech';
      const url = `${apiUrl}/organizations/download/by-document-direct?token=${encodeURIComponent(token)}&document_name=${encodeURIComponent(docName)}`;
      window.location.href = url;
    } catch (error) {
      alert('Failed to download documents.');
    } finally {
      setTimeout(() => setDownloadingDocType(null), 3000);
    }
  };

  const handleDownloadAllByType = () => {
    setDownloadingAllByType(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://orgpass.tech';
      const url = `${apiUrl}/organizations/download/all-by-type-direct?token=${encodeURIComponent(token)}`;
      window.location.href = url;
    } catch (error) {
      alert('Failed to download all documents.');
    } finally {
      setTimeout(() => setDownloadingAllByType(false), 3000);
    }
  };

  const handleDownloadOrgList = async () => {
    setDownloadingOrgList(true);
    try {
      const response = await downloadOrgList();
      // Year label is optional: if there is no active year the filename just omits it
      let yearPart = '';
      try {
        const yr = await getCurrentAcademicYear();
        if (yr.data?.year_label) {
          yearPart = `-${String(yr.data.year_label).replace(/[^A-Za-z0-9_-]+/g, '-')}`;
        }
      } catch (e) { /* no active academic year */ }
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Organizations-Report${yearPart}-${today}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to download organizations report.');
    } finally {
      setDownloadingOrgList(false);
    }
  };

  const handleDownloadPerOrg = () => {
    setDownloadingAll(true);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://orgpass.tech';
      const url = `${apiUrl}/organizations/download-all-direct?token=${encodeURIComponent(token)}`;
      window.location.href = url;
    } catch (error) {
      alert('Failed to download organizations.');
    } finally {
      setTimeout(() => setDownloadingAll(false), 3000);
    }
  };

  if (loading) {
    return <AdminOrganizationsSkeleton rows={6} />;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-[28px] font-bold text-gray-900 tracking-tight">
        All Organizations ({orgData.length})
      </h2>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
        <div className="flex items-center space-x-3 w-full md:w-auto">
          <div className="relative w-full md:w-[400px]">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icons.Search />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search by organization name..."
              className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm font-medium"
            />
          </div>
          <div className="relative">
            <button
              onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
              className="appearance-none bg-white border border-gray-300 text-gray-700 py-2.5 pl-4 pr-10 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-sm font-medium w-48 text-left relative flex items-center justify-between"
            >
              <span>{selectedFilter}</span>
              <Icons.ChevronDown />
            </button>

            {filterDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                <div
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm text-gray-700 transition-colors"
                  onClick={() => {
                    setSelectedFilter("All Status");
                    setFilterDropdownOpen(false);
                    setCurrentPage(1);
                  }}
                >
                  All Status
                </div>
                <div
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm text-gray-700 transition-colors"
                  onClick={() => {
                    setSelectedFilter("Accredited");
                    setFilterDropdownOpen(false);
                    setCurrentPage(1);
                  }}
                >
                  Accredited
                </div>
                <div
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm text-gray-700 transition-colors"
                  onClick={() => {
                    setSelectedFilter("Re-accredited");
                    setFilterDropdownOpen(false);
                    setCurrentPage(1);
                  }}
                >
                  Re-accredited
                </div>
                <div
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm text-gray-700 transition-colors"
                  onClick={() => {
                    setSelectedFilter("For Interview");
                    setFilterDropdownOpen(false);
                    setCurrentPage(1);
                  }}
                >
                  For Interview
                </div>

                {/* College nested */}
                <div
                  className={`relative px-4 py-2 cursor-pointer text-sm transition-colors flex justify-between items-center group ${activeSubmenu === "college" ? "bg-sidebar text-white" : "hover:bg-gray-100 text-gray-700"}`}
                  onMouseEnter={() => setActiveSubmenu("college")}
                  onMouseLeave={() => setActiveSubmenu(null)}
                >
                  <span>College</span>
                  <Icons.ChevronRight />

                  {activeSubmenu === "college" && (
                    <div className="absolute top-0 left-full ml-0 w-48 bg-sidebar text-white border border-sidebar shadow-lg py-1 z-50 rounded-md">
                      {[
                        "CABHA",
                        "CAG",
                        "CAM",
                        "CAS",
                        "CEN",
                        "CIT",
                        "CTE",
                        "Graduate School",
                        "SRSCO",
                      ].map((col) => (
                        <div
                          key={col}
                          className="px-4 py-2 hover:bg-white/10 cursor-pointer text-sm transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFilter(col);
                            setFilterDropdownOpen(false);
                            setActiveSubmenu(null);
                            setCurrentPage(1);
                          }}
                        >
                          {col}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Campus nested */}
                <div
                  className={`relative px-4 py-2 cursor-pointer text-sm transition-colors flex justify-between items-center group ${activeSubmenu === "campus" ? "bg-sidebar text-white" : "hover:bg-gray-100 text-gray-700"}`}
                  onMouseEnter={() => setActiveSubmenu("campus")}
                  onMouseLeave={() => setActiveSubmenu(null)}
                >
                  <span>Campus</span>
                  <Icons.ChevronRight />

                  {activeSubmenu === "campus" && (
                    <div className="absolute top-0 left-full ml-0 w-48 bg-sidebar text-white border border-sidebar shadow-lg py-1 z-50 rounded-md">
                      {[
                        "Alabat",
                        "Catanauan",
                        "Gumaca",
                        "Infanta",
                        "Lucena",
                        "Polillo",
                        "Tayabas",
                        "Tiaong",
                        "Tagkawayan",
                      ].map((cam) => (
                        <div
                          key={cam}
                          className="px-4 py-2 hover:bg-white/10 cursor-pointer text-sm transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFilter(cam);
                            setFilterDropdownOpen(false);
                            setActiveSubmenu(null);
                            setCurrentPage(1);
                          }}
                        >
                          {cam}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto justify-end">
          <button
            onClick={() => setDownloadModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-[13px] font-bold border-2 border-[#054334] text-[#054334] bg-white hover:bg-[#054334] hover:text-white transition-all"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download All Files
          </button>
        </div>
      </div>

      <div className="bg-white rounded-[12px] border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse table-fixed min-w-[720px]">
            <thead>
              <tr className="bg-[#086C53] text-white">
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-[26%] min-w-[170px] rounded-tl-[12px]">
                  Representative
                </th>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-[20%] min-w-[140px]">
                  Organization
                </th>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-[24%] min-w-[160px]">
                  Email
                </th>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider w-[14%] min-w-[100px]">
                  College
                </th>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-center w-[8%] min-w-[70px]">
                  Status
                </th>
                <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-right w-[8%] min-w-[70px] rounded-tr-[12px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {currentData.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-10 text-center text-gray-400 text-sm"
                  >
                    No organizations found.
                  </td>
                </tr>
              ) : (
                currentData.map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-full bg-sidebar text-white flex items-center justify-center text-xs font-bold shrink-0">
                          {row.repInitials}
                        </div>
                        <span className="text-sm font-medium text-gray-700 truncate" title={row.repName}>
                          {row.repName}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div
                        className="text-sm font-medium text-gray-700 break-words line-clamp-2 leading-snug"
                        title={row.orgFullName || row.org}
                      >
                        {row.org}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-500 truncate" title={row.email}>
                        {row.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-600">
                      {row.college}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span
                        className={`inline-block w-2.5 h-2.5 rounded-full ${row.status === "Accredited"
                          ? "bg-green-500"
                          : row.status === "Re-accredited"
                            ? "bg-emerald-800"
                            : "bg-blue-500"
                          }`}
                        title={row.status}
                      ></span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (actionMenuOrg?.id === row.id) {
                              setActionMenuOrg(null);
                              return;
                            }
                            const rect = e.currentTarget.getBoundingClientRect();
                            const dropdownHeight = 230;
                            const spaceBelow = window.innerHeight - rect.bottom;
                            const flip = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
                            setActionMenuPos({
                              x: rect.right,
                              y: flip ? rect.top : rect.bottom,
                              flip,
                            });
                            setActionMenuOrg(row);
                          }}
                          className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ml-auto ${actionMenuOrg?.id === row.id
                            ? "bg-gray-200 text-gray-800"
                            : "text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                            }`}
                          title="Actions"
                        >
                          <Icons.DotsVertical />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-center pb-8 mt-8">
        <div className="flex items-center space-x-2 bg-white rounded-xl px-3 py-1.5 shadow-sm border border-gray-100">
          <button
            onClick={handlePrev}
            disabled={currentPage === 1}
            className={`w-9 h-9 flex items-center justify-center rounded-[10px] border-2 transition-all ${currentPage === 1 ? "border-gray-300 text-gray-300" : "border-gray-500 text-gray-500 hover:border-gray-600 hover:text-gray-700"}`}
          >
            <Icons.ChevronLeft />
          </button>
          {[...Array(totalPages)].map((_, idx) => {
            const page = idx + 1;
            const isActive = page === currentPage;
            return (
              <button
                key={page}
                onClick={() => handlePageClick(page)}
                className={`w-9 h-9 flex items-center justify-center text-[14px] font-medium rounded-[10px] transition-all ${isActive
                  ? "bg-[#125845] text-white border-[3px] border-[#91c3b1] shadow-sm"
                  : "bg-white text-gray-600 border-2 border-gray-500 hover:border-gray-600 hover:text-gray-800"
                  }`}
              >
                {page}
              </button>
            );
          })}
          <button
            onClick={handleNext}
            disabled={currentPage === totalPages}
            className={`w-9 h-9 flex items-center justify-center rounded-[10px] border-2 transition-all ${currentPage === totalPages ? "border-gray-300 text-gray-300" : "border-gray-500 text-gray-500 hover:border-gray-600 hover:text-gray-700"}`}
          >
            <Icons.ChevronRight />
          </button>
        </div>
      </div>

      {/* View Organization Modal */}
      {selectedOrg &&
        createPortal(
          <div className="fixed top-0 left-0 w-screen h-screen z-[9999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-[16px] shadow-2xl w-full max-w-lg overflow-hidden border border-gray-100">
              <div className="p-8">
                <h3 className="text-[22px] font-bold text-gray-900 mb-1">
                  Organization Details
                </h3>
                <p className="text-gray-400 text-sm font-medium mb-6">
                  Organization ID: {selectedOrg.id}
                </p>

                <h4 className="font-bold text-gray-900 text-base mb-4">
                  Personal Information
                </h4>
                <div className="space-y-4 mb-8">
                  <div>
                    <p className="text-gray-400 text-sm font-medium mb-1">
                      Full Name
                    </p>
                    <p className="text-gray-900 font-medium">
                      {selectedOrg.repName}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm font-medium mb-1">
                      Email
                    </p>
                    <p className="text-gray-900 font-medium">
                      {selectedOrg.email}
                    </p>
                  </div>
                </div>

                <h4 className="font-bold text-gray-900 text-base mb-4">
                  Organization Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-6 mb-8">
                  <div>
                    <p className="text-gray-400 text-sm font-medium mb-1">
                      Organization Name
                    </p>
                    <p className="text-gray-900 font-medium leading-snug">
                      {selectedOrg.orgFullName}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm font-medium mb-1">
                      Acronym
                    </p>
                    <p className="text-gray-900 font-medium">
                      {selectedOrg.acronym || "N/A"}
                    </p>
                  </div>
                  {selectedOrg.orgCategory && (
                    <div>
                      <p className="text-gray-400 text-sm font-medium mb-1">
                        Type of Organization
                      </p>
                      <p className="text-gray-900 font-medium">
                        {selectedOrg.orgCategory}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-gray-400 text-sm font-medium mb-1">
                      College
                    </p>
                    <p className="text-gray-900 font-medium">
                      {selectedOrg.college}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm font-medium mb-1">
                      Position
                    </p>
                    <p className="text-gray-900 font-medium">President</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm font-medium mb-1">
                      President Contact Number
                    </p>
                    <p className="text-gray-900 font-medium">
                      {selectedOrg.presidentContact}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm font-medium mb-1">
                      Adviser Name
                    </p>
                    <p className="text-gray-900 font-medium">
                      {selectedOrg.adviserName}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm font-medium mb-1">
                      Adviser Contact Number
                    </p>
                    <p className="text-gray-900 font-medium">
                      {selectedOrg.adviserContact}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm font-medium mb-1">
                      Total Number of Members
                    </p>
                    <p className="text-gray-900 font-medium">
                      {selectedOrg.totalMembers}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm font-medium mb-1">
                      Status
                    </p>
                    <p className="text-gray-900 font-medium capitalize">
                      {selectedOrg.status}
                    </p>
                  </div>
                </div>

                <hr className="border-gray-100 mb-6" />

                <div className="flex justify-end">
                  <button
                    onClick={() => setSelectedOrg(null)}
                    className="bg-[#086C53] hover:bg-[#065440] text-white font-semibold py-2.5 px-8 rounded-lg transition-colors shadow-sm text-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Edit Organization Modal */}
      {editingOrg &&
        createPortal(
          <div className="fixed top-0 left-0 w-screen h-screen z-[9999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-[16px] shadow-2xl w-full max-w-lg overflow-hidden border border-gray-100">
              <div className="p-8">
                <h3 className="text-[22px] font-bold text-gray-900 mb-6">
                  Edit Organization
                </h3>

                <div className="space-y-4 mb-8">
                  <div>
                    <label className="block text-gray-700 text-sm font-semibold mb-2">
                      Organization Name
                    </label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) =>
                        setEditForm({ ...editForm, name: e.target.value })
                      }
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#086C53]/20 focus:border-[#086C53]"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 text-sm font-semibold mb-2">
                        Acronym
                      </label>
                      <input
                        type="text"
                        value={editForm.acronym}
                        onChange={(e) =>
                          setEditForm({ ...editForm, acronym: e.target.value })
                        }
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#086C53]/20 focus:border-[#086C53]"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 text-sm font-semibold mb-2">
                        College
                      </label>
                      <input
                        type="text"
                        value={editForm.college}
                        onChange={(e) =>
                          setEditForm({ ...editForm, college: e.target.value })
                        }
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#086C53]/20 focus:border-[#086C53]"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-semibold mb-2">
                      Contact Email
                    </label>
                    <input
                      type="email"
                      value={editForm.contact_email}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          contact_email: e.target.value,
                        })
                      }
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#086C53]/20 focus:border-[#086C53]"
                    />
                  </div>
                </div>

                <hr className="border-gray-100 mb-6" />

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setEditingOrg(null)}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 px-6 rounded-lg transition-colors text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    className="bg-[#086C53] hover:bg-[#065440] text-white font-semibold py-2.5 px-8 rounded-lg transition-colors shadow-sm text-sm"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Delete Organization Modal */}
      {deletingOrg &&
        createPortal(
          <div className="fixed top-0 left-0 w-screen h-screen z-[9999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
            <div className="bg-white rounded-[16px] shadow-2xl w-full max-w-sm overflow-hidden border border-gray-100 text-center p-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 text-red-600">
                <Icons.Trash />
              </div>
              <h3 className="text-[20px] font-bold text-gray-900 mb-2">
                Delete Organization
              </h3>
              <p className="text-gray-500 text-sm font-medium mb-8">
                Are you sure you want to remove{" "}
                <span className="font-bold text-gray-700">{deletingOrg.org}</span>
                ?
              </p>

              <div className="flex space-x-3">
                <button
                  onClick={() => !isDeleting && setDeletingOrg(null)}
                  disabled={isDeleting}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 font-semibold py-2.5 rounded-lg transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                  className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg transition-colors shadow-sm text-sm flex items-center justify-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8H4z"
                        ></path>
                      </svg>
                      <span>Deleting...</span>
                    </>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* 3-Dots Action Dropdown Menu */}
      {actionMenuOrg &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[9990]"
              onClick={() => setActionMenuOrg(null)}
            />
            <div
              className="fixed z-[9991] w-52 bg-white border border-gray-200 rounded-xl shadow-xl py-1 text-left text-gray-700 text-sm overflow-hidden"
              style={{
                top: actionMenuPos.flip
                  ? `${actionMenuPos.y - 4}px`
                  : `${actionMenuPos.y + 4}px`,
                transform: actionMenuPos.flip ? "translateY(-100%)" : "none",
                left: `${Math.max(16, actionMenuPos.x - 208)}px`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* View Details */}
              <button
                type="button"
                className="w-full px-4 py-2.5 hover:bg-gray-50 flex items-center space-x-2.5 transition-colors text-left text-gray-700"
                onClick={() => {
                  setSelectedOrg(actionMenuOrg);
                  setActionMenuOrg(null);
                }}
              >
                <Icons.Eye />
                <span className="font-medium text-[13px]">View Details</span>
              </button>

              {/* Edit Organization */}
              <button
                type="button"
                className="w-full px-4 py-2.5 hover:bg-gray-50 flex items-center space-x-2.5 transition-colors text-left text-gray-700"
                onClick={() => {
                  openEditModal(actionMenuOrg);
                  setActionMenuOrg(null);
                }}
              >
                <Icons.Edit />
                <span className="font-medium text-[13px]">Edit Organization</span>
              </button>

              {/* Download Details */}
              <button
                type="button"
                className="w-full px-4 py-2.5 hover:bg-gray-50 flex items-center space-x-2.5 transition-colors text-left text-gray-700"
                onClick={() => {
                  handleDownloadOrg(actionMenuOrg.id, actionMenuOrg.orgFullName);
                  setActionMenuOrg(null);
                }}
              >
                {downloadingOrgId === actionMenuOrg.id ? (
                  <svg className="w-4 h-4 animate-spin text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                )}
                <span className="font-medium text-[13px]">Download Files</span>
              </button>

              {/* Accreditation Certificate */}
              <button
                type="button"
                className="w-full px-4 py-2.5 hover:bg-gray-50 flex items-center space-x-2.5 transition-colors text-left text-gray-700"
                onClick={() => {
                  const targetOrg = actionMenuOrg;
                  setActionMenuOrg(null);
                  setAccredCertModalOrg(targetOrg);
                  fetchAccredCert(targetOrg.organization_id || targetOrg.id);
                }}
              >
                <svg
                  className={`w-4 h-4 ${accredCerts[actionMenuOrg.organization_id || actionMenuOrg.id] ? "text-green-600" : "text-gray-500"}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="font-medium text-[13px]">
                  {accredCerts[actionMenuOrg.organization_id || actionMenuOrg.id]
                    ? "Certificate"
                    : "Upload Certificate"}
                </span>
              </button>

              <div className="border-t border-gray-100 my-1" />

              {/* Delete Organization */}
              <button
                type="button"
                className="w-full px-4 py-2.5 hover:bg-red-50 flex items-center space-x-2.5 transition-colors text-left text-red-600"
                onClick={() => {
                  setDeletingOrg(actionMenuOrg);
                  setActionMenuOrg(null);
                }}
              >
                <Icons.Trash />
                <span className="font-medium text-[13px]">Delete</span>
              </button>
            </div>
          </>,
          document.body,
        )}

      {/* Accreditation Certificate Modal */}
      {accredCertModalOrg &&
        createPortal(
          <div
            className="fixed top-0 left-0 w-screen h-screen z-[9999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
            onClick={() => setAccredCertModalOrg(null)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-[18px] font-bold text-gray-900">Accreditation Certificate</h3>
                  <p className="text-[13px] text-gray-500 mt-0.5">
                    {accredCertModalOrg.name || accredCertModalOrg.orgFullName}
                  </p>
                </div>
                <button
                  onClick={() => setAccredCertModalOrg(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {accredCerts[accredCertModalOrg.organization_id || accredCertModalOrg.id] && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-3 mb-4">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-[13px] font-bold text-green-700">Certificate Uploaded</p>
                    <p className="text-[11px] text-green-600">
                      {accredCerts[accredCertModalOrg.organization_id || accredCertModalOrg.id].uploaded_at
                        ? new Date(accredCerts[accredCertModalOrg.organization_id || accredCertModalOrg.id].uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        : 'Uploaded'}
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      if (!window.confirm('Delete this Accreditation Certificate?')) return;
                      setAccredCertDeleting(true);
                      try {
                        const orgId = accredCertModalOrg.organization_id || accredCertModalOrg.id;
                        await deleteAccreditationCertificate(orgId);
                        setAccredCerts(prev => ({ ...prev, [orgId]: null }));
                      } catch (error) {
                        alert('Failed to delete certificate.');
                      } finally {
                        setAccredCertDeleting(false);
                      }
                    }}
                    disabled={accredCertDeleting}
                    className="px-3 py-1.5 rounded-lg text-[12px] font-bold border border-red-200 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60"
                  >
                    {accredCertDeleting ? '...' : 'Delete'}
                  </button>
                </div>
              )}

              {!accredCerts[accredCertModalOrg.organization_id || accredCertModalOrg.id] && (
                <p className="text-[13px] text-gray-500 mb-4">
                  Upload the Accreditation Certificate after the organization has been officially accredited.
                </p>
              )}

              <AccreditationCertUploadDropzone
                orgId={accredCertModalOrg.organization_id || accredCertModalOrg.id}
                accredCerts={accredCerts}
                accredCertUploading={accredCertUploading}
                fetchAccredCert={fetchAccredCert}
                setAccredCertUploading={setAccredCertUploading}
              />
            </div>
          </div>,
          document.body,
        )}

      {downloadModalOpen &&
        createPortal(
          <div
            className="fixed top-0 left-0 w-screen h-screen z-[9999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
            onClick={() => setDownloadModalOpen(false)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
                <h3 className="text-[18px] font-bold text-gray-900">Download Documents</h3>
                <button
                  onClick={() => setDownloadModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Top Action Buttons */}
              <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                <button
                  onClick={handleDownloadAllByType}
                  disabled={downloadingAllByType}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-[13px] font-bold bg-[#054334] text-white hover:bg-[#086C53] transition-colors disabled:opacity-60"
                >
                  {downloadingAllByType ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  )}
                  {downloadingAllByType ? "Downloading..." : "Download All"}
                </button>

                <button
                  onClick={handleDownloadPerOrg}
                  disabled={downloadingAll}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-[13px] font-bold border-2 border-[#054334] text-[#054334] bg-white hover:bg-[#054334] hover:text-white transition-all disabled:opacity-60"
                >
                  {downloadingAll ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  )}
                  {downloadingAll ? "Downloading..." : "Download per Organization"}
                </button>

                <button
                  onClick={handleDownloadOrgList}
                  disabled={downloadingOrgList}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-[13px] font-bold border-2 border-gray-300 text-gray-600 bg-white hover:bg-gray-50 transition-all disabled:opacity-60"
                >
                  {downloadingOrgList ? (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  )}
                  {downloadingOrgList ? "Downloading..." : "List (PDF)"}
                </button>
              </div>

              {/* Document List */}
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
                <p className="text-[12px] font-bold text-gray-400 uppercase tracking-wider mb-3">
                  Download by Document Type
                </p>
                {ACCREDITATION_DOCUMENTS.map((docName) => (
                  <div
                    key={docName}
                    className="flex items-center justify-between py-2.5 px-4 rounded-xl bg-gray-50 border border-gray-100 hover:bg-gray-100 transition-colors"
                  >
                    <span className="text-[14px] font-medium text-gray-700">{docName}</span>
                    <button
                      onClick={() => handleDownloadByDocType(docName)}
                      disabled={downloadingDocType === docName}
                      className="p-1.5 rounded-lg text-gray-500 hover:text-[#054334] hover:bg-[#e0f5ee] transition-colors disabled:opacity-60"
                      title={`Download all ${docName} submissions`}
                    >
                      {downloadingDocType === docName ? (
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default Organizations;
